import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useReactRouter from 'use-react-router'
import { routeManager } from 'core/utils/routes'
import DocumentMeta from 'core/components/DocumentMeta'
import Text from 'core/elements/Text'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import TenantRolesTableFieldDefault from '../../userManagement/users/TenantRolesTableField'
import { requiredValidator } from 'core/utils/fieldValidators'
import { listTenants } from '../../userManagement/tenants/new-actions'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { tenantsSelector } from 'account/components/userManagement/tenants/selectors'
import Progress from 'core/components/progress/Progress'
import {
  groupFormSubmission,
  loadGroupRoleAssignments,
  listMngmGroups,
  listMngmGroupMappings,
} from './new-actions'
import GroupSettingsFields from './GroupSettingsFields'
import GroupCustomMappingsFields from './GroupCustomMappingsFields'
import { propEq } from 'ramda'
import { emptyObj, noop, pathStr } from 'utils/fp'
import uuid from 'uuid'
import { Criteria } from './CriteriaPicklist'
import GroupsTips from './GroupsTips'
import ModalForm from 'core/elements/modal/ModalForm'
import Column from 'core/containers/Column'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import { groupsSelector, groupMappingsSelector } from './selectors'
import { Group } from './model'
import { defaultGroupMappingId } from 'app/constants'
import { getIDPName } from 'api-client/helpers'
import { listSsoAttributes } from '../attributes/actions'
import { ssoAttributesSelector } from '../attributes/selectors'
import { SSO_ATTRIBUTE_PLACEHOLDER_REGEX } from '../attributes/helpers'

const TenantRolesTableField: any = TenantRolesTableFieldDefault // types on forward ref .js file dont work well.

const useStyles = makeStyles((theme: Theme) => ({
  validatedFormContainer: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
}))

const tenantRolesValidations = [requiredValidator.withMessage('Must select at least one tenant')]

const getMappingCriteria = (mapping) =>
  [Criteria.AnyOneOf, Criteria.DoesNotContain].find((type) => mapping.hasOwnProperty(type)) || ''

// Build a mapping template string (e.g. "{userID} {username}" or "{email}")
// by reversing the mappingRule's positional placeholders back to attribute keys.
// This preserves any separators (spaces, dashes, @, etc.) present in the original template.
const buildAttributeMappingTemplate = (mapping, accessor: 'name' | 'email') => {
  if (!mapping) return ''
  const raw = mapping.local?.[0]?.user?.[accessor] || ''
  const rebuilt = raw.replace(SSO_ATTRIBUTE_PLACEHOLDER_REGEX, (match) => {
    const remote_attribute_idx = Number(match.slice(1, -1))
    const type = mapping?.remote?.[remote_attribute_idx]?.type
    return type ? `{${type}}` : ''
  })
  return rebuilt
}

const getCustomMappings = (mapping) => {
  if (!mapping || !mapping.local || !mapping.remote) return []
  // Get the last email attribute index
  const emailTemplate = mapping.local?.[0]?.user?.email || ''
  let lastEmailAttributeIdx = -1
  const matches = emailTemplate.match(SSO_ATTRIBUTE_PLACEHOLDER_REGEX)
  if (matches) {
    lastEmailAttributeIdx = Number(matches[matches.length - 1].slice(1, -1))
  }

  // Get the custom mappings starting from the last email attribute index
  const customMappingStartIdx = lastEmailAttributeIdx >= 0 ? lastEmailAttributeIdx + 1 : 0

  return (
    mapping.remote?.slice(customMappingStartIdx).map((mapping) => ({
      id: uuid.v4(),
      attribute: mapping.type,
      criteria: getMappingCriteria(mapping),
      values: mapping[getMappingCriteria(mapping)]?.join(', ') || '',
      regexMatching: mapping.regex,
    })) || []
  )
}

const getUsernameAttributeMappingTemplate = (mapping) =>
  buildAttributeMappingTemplate(mapping, 'name')
const getEmailAttributeMappingTemplate = (mapping) =>
  buildAttributeMappingTemplate(mapping, 'email')

const EditGroupPage = () => {
  const { match, history } = useReactRouter()
  const classes = useStyles({})
  const groupId = match.params.id

  const { loading: loadingTenants } = useListAction(listTenants)
  const tenants = useSelector(tenantsSelector)

  const { loading: loadingGroups } = useListAction(listMngmGroups)
  const groups = useSelector(groupsSelector)

  const { loading: loadingGroupMappings } = useListAction(listMngmGroupMappings)
  const groupMappings = useSelector(groupMappingsSelector)

  const { loading: loadingSsoAttributes } = useListAction(listSsoAttributes)
  const ssoAttributes = useSelector(ssoAttributesSelector)
  const ssoAttributeKeys = ssoAttributes?.[0]?.attributeKeys || []

  const group = useMemo(() => groups.find(propEq('id', groupId)) || (emptyObj as Group), [
    groups,
    groupId,
  ])
  const [roleAssignments, setRoleAssignments] = useState(null)
  const [loadingRoleAssignments, setLoadingRoleAssignments] = useState(true)
  const [submitting, updateSubmitting] = useState(false)
  const existingMapping = groupMappings.find((m) => m.id === defaultGroupMappingId(getIDPName()))

  const mappingRule = useMemo(() => {
    if (!existingMapping) {
      return null
    }
    return existingMapping?.rules?.find(
      (rule) =>
        group.id === rule.local?.[0]?.group?.id || group.name === rule.local?.[0]?.group?.name,
    )
    // Needed to add groupId as a dependency bc the modal loads in the background on list page
  }, [groupMappings, group, existingMapping])

  const preSelectedRoleAssignments = useMemo(
    () =>
      roleAssignments?.reduce(
        (acc, roleAssignment) => ({
          ...acc,
          [pathStr('scope.project.id', roleAssignment)]: pathStr('role.id', roleAssignment),
        }),
        {},
      ),
    [roleAssignments],
  )

  const initialContext = useMemo(
    () => ({
      groupId: groupId,
      name: group.name,
      description: group.description,
      usernameAttributeMappingTemplate: getUsernameAttributeMappingTemplate(mappingRule),
      emailAttributeMappingTemplate: getEmailAttributeMappingTemplate(mappingRule),
      customMappings: getCustomMappings(mappingRule),
      roleAssignments: preSelectedRoleAssignments,
    }),
    [group, mappingRule, roleAssignments],
  )

  useEffect(() => {
    const loadRoles = async () => {
      setLoadingRoleAssignments(true)
      const roles = await loadGroupRoleAssignments(groupId)
      setRoleAssignments(roles)
      setLoadingRoleAssignments(false)
    }

    loadRoles()
  }, [groupId])

  const loadingSomething =
    loadingGroups ||
    loadingGroupMappings ||
    !roleAssignments ||
    loadingRoleAssignments ||
    loadingSsoAttributes

  const submitForm = useCallback(
    async (params) => {
      updateSubmitting(true)
      await groupFormSubmission({
        params: {
          ...params,
          // prevRoleAssignmentsArr & groupId not being passed through submit fn in ValidatedForm
          group,
          prevRoleAssignmentsArr: roleAssignments,
        },
        existingMapping,
        operation: 'update',
        protocolExists: true,
      })
      updateSubmitting(false)
      history.push(routeManager.routes.sso.root.path({ tab: 'groups' }))
    },
    [group, roleAssignments, existingMapping],
  )

  return (
    <>
      <DocumentMeta title="Edit Group" bodyClasses={['form-view']} />
      <ModalForm
        route={routeManager.routes.sso.editGroup}
        title="Edit Group"
        initialValues={initialContext}
        onBackdropClick={noop}
        onClose={() => history.push(routeManager.routes.sso.root.path({ tab: 'groups' }))}
        onSubmit={submitForm}
        submitTitle="Edit Group"
        loading={loadingSomething}
        submitting={submitting}
        loadingMessage={submitting ? 'Submitting form...' : 'Loading Group...'}
      >
        {
          ((({ setFieldValue, values }) => {
            return (
              <>
                <Column gap={16}>
                  <GroupsTips />
                  <FormFieldSection title="Group Settings" step={1}>
                    <Column gap={16}>
                      <GroupSettingsFields
                        setFieldValue={setFieldValue}
                        values={values}
                        groups={groups}
                        groupId={groupId}
                        ssoAttributeKeys={ssoAttributeKeys}
                      />
                    </Column>
                    <GroupCustomMappingsFields
                      id="customMappings"
                      setFieldValue={setFieldValue}
                      values={values}
                    />
                  </FormFieldSection>
                  <FormFieldSection title="Tenants & Roles" step={2}>
                    <Text variant="body2">
                      Specify what Tenants this Group should have access to, and what role the users
                      will be assigned in each Tenant.
                    </Text>
                    <Progress
                      renderContentOnMount={!loadingTenants}
                      loading={loadingTenants}
                      message={'Loading Tenants...'}
                    >
                      <TenantRolesTableField
                        validations={tenantRolesValidations}
                        id="roleAssignments"
                        tenants={tenants}
                        preSelectedTenantIds={preSelectedRoleAssignments}
                      />
                    </Progress>
                  </FormFieldSection>
                </Column>
              </>
            )
          }) as unknown) as React.ReactNode
        }
      </ModalForm>
    </>
  )
}

export default EditGroupPage
